import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  AlertColor,
  CircularProgress,
  Tooltip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Refresh,
  Search,
  Block,
  FilterList,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  CancelOutlined,
  Clear,
  Sort,
} from '@mui/icons-material';

import api from '../../../../services/api';

// --- INTERFACES ---
interface Branch {
  id: number;
  nome: string;
  sigla?: string;
}

interface Company {
  id: number;
  nome: string;
  subtitulo: string;
  filiais: Branch[];
}

interface ProductionQueueEntry {
  entryNumber: number;
  entryDate: string;
  entryTime: number;
  collectorName: string;
  status: number; // 0=Pendente, 1=Sucesso, 2=Erro, 3=Cancelado
  processDate?: string;
  processTime?: number;
  processMessage?: string;
  companyId: number;
  branchId: number;
  originCode: string;
  originName?: string;
  productionOrderNumber: number;
  stageCode: number;
  stageName?: string;
  routeSequence: number;
  operatorCode: number;
  operatorName?: string;
  producedQuantity: number;
  scrapQuantity: number;
  canceledBy?: number;
}

// Chaves para persistência no localStorage
const LS_KEY_COMPANY = 'queue_monitor_company_id';
const LS_KEY_BRANCH = 'queue_monitor_branch_id';
const LS_KEY_STATUSES = 'queue_monitor_statuses';

const STATUS_OPTIONS = [
  { value: 0, label: '0 - Pendente', color: 'warning' as const },
  { value: 1, label: '1 - Sucesso', color: 'success' as const },
  { value: 2, label: '2 - Erro', color: 'error' as const },
  { value: 3, label: '3 - Cancelado', color: 'default' as const },
];

const formatMinutesToTime = (totalMinutes?: number): string => {
  if (totalMinutes === undefined || totalMinutes === null) return '--:--';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatDateToBR = (dateStr?: string): string => {
  if (!dateStr) return '--/--/----';
  const cleanDate = dateStr.split('T')[0];
  const [year, month, day] = cleanDate.split('-');
  return `${day}/${month}/${year}`;
};

export default function ProductionQueueMonitor() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // REF para controlar o Scroll do container da tabela
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [entries, setEntries] = useState<ProductionQueueEntry[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(true);
  const [isCanceling, setIsCanceling] = useState<boolean>(false);

  // --- PAGINAÇÃO ---
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);

  // --- ESTADOS COM PERSISTÊNCIA (localStorage) ---
  const [companyId, setCompanyId] = useState<number>(() => {
    const saved = localStorage.getItem(LS_KEY_COMPANY);
    return saved ? Number(saved) : 1;
  });

  const [branchId, setBranchId] = useState<number>(() => {
    const saved = localStorage.getItem(LS_KEY_BRANCH);
    return saved ? Number(saved) : 1;
  });

  const [selectedStatuses, setSelectedStatuses] = useState<number[]>(() => {
    const saved = localStorage.getItem(LS_KEY_STATUSES);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        // Fallback
      }
    }
    return [0, 2];
  });

  // --- FILTROS DE TEXTO E DATA ---
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [originFilter, setOriginFilter] = useState<string>('');
  const [opFilter, setOpFilter] = useState<string>('');
  const [stageFilter, setStageFilter] = useState<string>('');
  const [sequenceFilter, setSequenceFilter] = useState<string>('');
  const [operatorFilter, setOperatorFilter] = useState<string>('');

  // --- ORDENAÇÃO ---
  const [sortOption, setSortOption] = useState<string>('entryNumber_desc');

  // Cancel Dialog & Notification
  const [cancelTargetNumber, setCancelTargetNumber] = useState<number | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    type: AlertColor;
    message: string;
  }>({ open: false, type: 'info', message: '' });

  const showNotification = (message: string, type: AlertColor = 'info') => {
    setNotification({ open: true, message, type });
  };

  // Persistência
  useEffect(() => {
    localStorage.setItem(LS_KEY_COMPANY, String(companyId));
  }, [companyId]);
  useEffect(() => {
    localStorage.setItem(LS_KEY_BRANCH, String(branchId));
  }, [branchId]);
  useEffect(() => {
    localStorage.setItem(LS_KEY_STATUSES, JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  // Carregar Empresas
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');
        if (data && Array.isArray(data) && data.length > 0) {
          setCompanyList(data);
          const exists = data.some(c => c.id === companyId);
          if (!exists) {
            setCompanyId(data[0].id);
            if (data[0].filiais?.length) setBranchId(data[0].filiais[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
        showNotification('Erro ao carregar lista de empresas e filiais.', 'error');
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // --- BUSCAR FILA DE APONTAMENTOS ---
  const fetchQueueData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [sortBy, sortOrder] = sortOption.split('_');

      const { data } = await api.get<ProductionQueueEntry[]>('app/data-collection/get-production-queue', {
        params: {
          companyId,
          branchId,
          status: selectedStatuses.join(','),
          startDate,
          endDate,
          origin: originFilter,
          productionOrder: opFilter,
          stage: stageFilter,
          sequence: sequenceFilter,
          operator: operatorFilter,
          sortBy,
          sortOrder,
        },
      });
      setEntries(data || []);
      setPage(0);

      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    } catch (error: any) {
      console.error('Error fetching queue:', error);
      const msg = error?.response?.data?.message || 'Erro ao carregar fila de apontamentos.';
      showNotification(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    companyId,
    branchId,
    selectedStatuses,
    startDate,
    endDate,
    originFilter,
    opFilter,
    stageFilter,
    sequenceFilter,
    operatorFilter,
    sortOption,
  ]);

  useEffect(() => {
    fetchQueueData();
  }, [companyId, branchId, selectedStatuses, sortOption]);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setOriginFilter('');
    setOpFilter('');
    setStageFilter('');
    setSequenceFilter('');
    setOperatorFilter('');
    setTimeout(() => fetchQueueData(), 50);
  };

  const handleCompanyChange = (e: SelectChangeEvent<number>) => {
    const selectedCompanyId = Number(e.target.value);
    setCompanyId(selectedCompanyId);
    const foundCompany = companyList.find(emp => emp.id === selectedCompanyId);
    if (foundCompany && foundCompany.filiais && foundCompany.filiais.length > 0) {
      setBranchId(foundCompany.filiais[0].id);
    }
  };

  const handleStatusChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    setSelectedStatuses(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const currentCompany = companyList.find(e => e.id === companyId) ||
    companyList[0] || {
      id: 1,
      nome: '',
      subtitulo: '',
      filiais: [],
    };

  const handleCancelEntry = async () => {
    if (!cancelTargetNumber) return;
    try {
      setIsCanceling(true);
      const { data } = await api.post('app/data-collection/cancel-production-entry', {
        entryNumber: cancelTargetNumber,
      });
      showNotification(data.message || 'Apontamento cancelado com sucesso.', 'success');
      setCancelTargetNumber(null);
      fetchQueueData();
    } catch (error: any) {
      console.error('Error canceling entry:', error);
      showNotification(error?.response?.data?.message || 'Erro ao cancelar o apontamento.', 'error');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  const renderStatusChip = (status: number) => {
    switch (status) {
      case 0:
        return (
          <Chip icon={<HourglassEmpty />} label="Pendente" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
        );
      case 1:
        return <Chip icon={<CheckCircle />} label="Sucesso" color="success" size="small" sx={{ fontWeight: 'bold' }} />;
      case 2:
        return <Chip icon={<ErrorIcon />} label="Erro" color="error" size="small" sx={{ fontWeight: 'bold' }} />;
      case 3:
        return (
          <Chip
            icon={<CancelOutlined />}
            label="Cancelado"
            size="small"
            sx={{
              bgcolor: theme.palette.action.disabledBackground,
              color: theme.palette.text.secondary,
              fontWeight: 'bold',
            }}
          />
        );
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  // --- MEMORIZAÇÃO DA TABELA ---
  const tableRows = useMemo(() => {
    const paginated = entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return paginated.map(row => {
      const canCancel = row.status === 0 || row.status === 2;

      return (
        <TableRow key={row.entryNumber} hover sx={{ '& td': { borderColor: theme.palette.divider } }}>
          <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>#{row.entryNumber}</TableCell>
          <TableCell align="center" sx={{ color: 'text.primary' }}>
            {formatDateToBR(row.entryDate)}
            <Typography variant="caption" display="block" color="text.secondary">
              {formatMinutesToTime(row.entryTime)}
            </Typography>
          </TableCell>
          <TableCell sx={{ color: 'text.primary' }}>
            <Typography variant="body2">
              {row.originCode}
              {row.originName && ` - ${row.originName}`}
            </Typography>
          </TableCell>
          <TableCell sx={{ color: 'text.primary' }}>{row.productionOrderNumber}</TableCell>
          <TableCell sx={{ color: 'text.primary' }}>
            <Typography variant="body2">
              {row.stageCode}
              {row.stageName && ` - ${row.stageName}`}
            </Typography>
          </TableCell>
          <TableCell align="center" sx={{ color: 'text.primary' }}>
            {row.routeSequence}
          </TableCell>
          <TableCell sx={{ color: 'text.primary' }}>
            <Typography variant="body2">
              {row.operatorCode}
              {row.operatorName ? ` - ${row.operatorName}` : 'Não identificado'}
            </Typography>
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {row.producedQuantity}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
            {row.scrapQuantity}
          </TableCell>
          <TableCell sx={{ color: 'text.primary' }}>{row.collectorName}</TableCell>
          <TableCell align="center">{renderStatusChip(row.status)}</TableCell>
          <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
            <Typography variant="caption" color={row.status === 2 ? 'error.main' : 'text.primary'}>
              {row.processMessage || 'Aguardando processamento...'}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Tooltip
              title={
                canCancel
                  ? 'Cancelar este apontamento da fila'
                  : 'Apenas registros pendentes ou com erro podem ser cancelados'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={!canCancel}
                  onClick={() => setCancelTargetNumber(row.entryNumber)}
                  startIcon={<Block />}
                  sx={{ fontWeight: 'bold' }}
                >
                  Cancelar
                </Button>
              </span>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  }, [entries, page, rowsPerPage, theme]);

  return (
    <Container maxWidth={false} sx={{ pb: 2, px: { xs: 1.5, sm: 2 } }}>
      {/* HEADER */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: isDark ? alpha(theme.palette.primary.main, 0.15) : theme.palette.primary.main,
          color: isDark ? theme.palette.primary.light : '#fff',
          border: isDark ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Monitoramento da Fila de Apontamento
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? 'text.secondary' : alpha('#fff', 0.85) }}>
              Acompanhamento de envios da fábrica para o ERP Senior Sapiens (USU_T900FOA)
            </Typography>
          </Box>
          <Button
            variant={isDark ? 'outlined' : 'contained'}
            color={isDark ? 'primary' : 'inherit'}
            onClick={fetchQueueData}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
            sx={{
              fontWeight: 'bold',
              bgcolor: isDark ? 'transparent' : '#fff',
              color: 'primary.main',
              '&:hover': {
                bgcolor: isDark ? alpha(theme.palette.primary.main, 0.1) : alpha('#fff', 0.9),
              },
            }}
          >
            Atualizar Fila
          </Button>
        </Box>
      </Paper>

      {/* PAINEL DE FILTROS */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          {/* EMPRESA, FILIAL E SITUAÇÕES */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-empresa-label">Empresa</InputLabel>
              <Select
                labelId="select-empresa-label"
                value={companyId}
                label="Empresa"
                onChange={handleCompanyChange}
                disabled={isLoadingCompanies}
              >
                {companyList.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.nome} ({emp.subtitulo})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-filial-label">Filial</InputLabel>
              <Select
                labelId="select-filial-label"
                value={branchId}
                label="Filial"
                onChange={e => setBranchId(Number(e.target.value))}
                disabled={isLoadingCompanies || !currentCompany.filiais?.length}
              >
                {currentCompany.filiais?.map(fil => (
                  <MenuItem key={fil.id} value={fil.id}>
                    {fil.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-status-label">Situações</InputLabel>
              <Select
                labelId="select-status-label"
                multiple
                value={selectedStatuses}
                onChange={handleStatusChange}
                input={<OutlinedInput label="Situações" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(val => {
                      const opt = STATUS_OPTIONS.find(o => o.value === val);
                      return (
                        <Chip
                          key={val}
                          label={opt ? opt.label.split('-')[1].trim() : val}
                          size="small"
                          color={opt?.color}
                          sx={{ height: 20, fontSize: '0.75rem', fontWeight: 'bold' }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedStatuses.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data Início"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data Fim"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          {/* ORIGEM, OP, ESTÁGIO, SEQUÊNCIA, OPERADOR */}
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Origem"
              placeholder="Cód ou Nome"
              value={originFilter}
              onChange={e => setOriginFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label="OP"
              placeholder="Nº OP"
              value={opFilter}
              onChange={e => setOpFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Estágio"
              placeholder="Cód ou Nome"
              value={stageFilter}
              onChange={e => setStageFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Sequência"
              placeholder="Seq."
              value={sequenceFilter}
              onChange={e => setSequenceFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Operador"
              placeholder="Cód ou Nome"
              value={operatorFilter}
              onChange={e => setOperatorFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchQueueData()}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-ordem-label">Ordernar por</InputLabel>
              <Select
                labelId="select-ordem-label"
                value={sortOption}
                label="Ordernar por"
                startAdornment={<Sort sx={{ color: 'action.active', mr: 1 }} />}
                onChange={e => setSortOption(e.target.value)}
              >
                <MenuItem value="entryNumber_desc">Nº Fila (Maior para Menor)</MenuItem>
                <MenuItem value="entryNumber_asc">Nº Fila (Menor para Maior)</MenuItem>
                <MenuItem value="date_desc">Data / Hora (Mais Recentes)</MenuItem>
                <MenuItem value="date_asc">Data / Hora (Mais Antigos)</MenuItem>
                <MenuItem value="route_asc">Origem + OP + Estágio + Sequência</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={fetchQueueData}
                startIcon={<Search />}
                sx={{ fontWeight: 'bold', minWidth: 120 }}
              >
                Filtrar
              </Button>

              <Tooltip title="Limpar Filtros">
                <IconButton size="small" onClick={handleClearFilters} color="inherit">
                  <Clear fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* BARRINHA INFORMATIVA */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, px: 0.5 }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
          Total de registros encontrados: <span style={{ color: theme.palette.primary.main }}>{entries.length}</span>
        </Typography>
      </Box>

      {/* PAPER CONTAINER DA TABELA */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TableContainer
          ref={tableContainerRef}
          sx={{
            maxHeight: 'calc(100vh - 340px)',
            minHeight: '320px',
            overflowY: 'auto',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    color: 'text.primary',
                    fontWeight: 'bold',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell>Nº Fila</TableCell>
                <TableCell align="center">Data / Hora</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>OP</TableCell>
                <TableCell>Estágio</TableCell>
                <TableCell align="center">Seq.</TableCell>
                <TableCell>Operador</TableCell>
                <TableCell align="right">Qtd. Boas</TableCell>
                <TableCell align="right">Qtd. Refugo</TableCell>
                <TableCell>Coletor</TableCell>
                <TableCell align="center">Situação</TableCell>
                <TableCell>Retorno do ERP / Observação</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Carregando registros da fila...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                    <FilterList sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                      Nenhum apontamento encontrado para os filtros selecionados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINAÇÃO FIXA */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 250]}
            component="div"
            count={entries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Box>
      </Paper>

      {/* DIÁLOGO DE CANCELAMENTO */}
      <Dialog open={Boolean(cancelTargetNumber)} onClose={() => setCancelTargetNumber(null)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Cancelamento</DialogTitle>
        <DialogContent>
          <DialogContentText color="text.secondary">
            Deseja realmente cancelar o apontamento <b>Nº #{cancelTargetNumber}</b> na fila?
            <br />
            Esta ação alterará a situação para <b>3 - Cancelado</b> e registrará seu usuário do ERP para auditoria.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelTargetNumber(null)} color="inherit" disabled={isCanceling}>
            Voltar
          </Button>
          <Button
            onClick={handleCancelEntry}
            color="error"
            variant="contained"
            disabled={isCanceling}
            startIcon={isCanceling ? <CircularProgress size={18} color="inherit" /> : <Block />}
            sx={{ fontWeight: 'bold' }}
          >
            Confirmar Cancelamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.type}
          variant="filled"
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          sx={{ fontWeight: 'bold' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
